import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { fetchAppUsers, removeAppUser, setAppUserRole, type AppUser } from '../../api/owner';
import { BLOCK_EXPLAINER, personLine, signInSummary, type Accounts, type SiteReach } from '../../state/people';
import { Button, EmptyState, Screen } from '../../ui';

/**
 * The people using a published site — end users, not builder members.
 * The web's counterpart of the phone's People tab (src/webapp/WebAppAdminPanel.tsx), in its words.
 *
 * Block and Remove are deliberately different acts, not two words for one. Blocking keeps what
 * a person saved and stops their account saving, loading or using AI on the site, and is
 * reversible. Removing takes their data with them, because an owner who removes a person means it.
 */
export default function Users() {
  const { appId = '' } = useParams();
  /*
   * Whether the site has sign-in is read off its code, which this route does not carry; the
   * project editor, which has it, hands the answer over. Opened any other way (a bookmark, My
   * Apps), the screen says nothing about sign-in rather than guess.
   */
  const handed = useLocation().state as { accounts?: Accounts; reach?: SiteReach | null } | null;
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<{ person: AppUser; act: 'block' | 'remove' } | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setUsers(await fetchAppUsers(appId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the people using this site.");
    } finally {
      setLoaded(true);
    }
  }, [appId]);

  useEffect(() => {
    void load();
  }, [load]);

  const who = (person: AppUser) => person.email ?? 'this person';

  async function act(person: AppUser, run: () => Promise<void>): Promise<void> {
    setBusy(person.userId);
    setError(null);
    setNotice(null);
    try {
      await run();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't do that. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  function ask(person: AppUser, which: 'block' | 'remove'): void {
    setPending({ person, act: which });
    dialog.current?.showModal();
  }

  function confirm(): void {
    const chosen = pending;
    dialog.current?.close();
    setPending(null);
    if (!chosen) return;
    const { person } = chosen;
    if (chosen.act === 'block') {
      void act(person, () => setAppUserRole(appId, person.userId, 'blocked'));
      return;
    }
    void act(person, async () => {
      const { records } = await removeAppUser(appId, person.userId);
      setNotice(records > 0 ? `Removed, along with ${records} ${records === 1 ? 'item' : 'items'} of their data.` : 'Removed. They had nothing stored.');
    });
  }

  // The owner is always a member of their own site, so one row is not "somebody signed up".
  const visitors = users.filter((u) => u.role !== 'owner');
  const summary = handed?.accounts ? signInSummary(handed.accounts, handed.reach ?? null) : null;

  return (
    <Screen title="People">
      {summary && (
        <div className="card clay-card people-status" role="status">
          <p className="type-heading">{summary.headline}</p>
          <p className="type-body">{summary.detail}</p>
        </div>
      )}
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
      {notice && <p role="status" className="type-caption">{notice}</p>}

      {!loaded ? (
        <p className="type-body">Loading…</p>
      ) : visitors.length === 0 ? (
        // With no sign-in there is nobody to wait for; the status above already says so.
        handed?.accounts === 'none' ? null : (
          <EmptyState icon="account" title="Nobody has signed in yet" body="Share the link — anyone who signs in appears here." />
        )
      ) : (
        <ul className="project-list" aria-label={`${visitors.length} ${visitors.length === 1 ? 'person has' : 'people have'} signed in`}>
          {visitors.map((u) => {
            const blocked = u.role === 'blocked';
            return (
              <li key={u.userId} className="card clay-card project-row">
                <span className="project-text">
                  <span className="type-heading">{u.email ?? 'Signed-in visitor'}</span>
                  <span className="type-caption project-meta">{personLine(u)}</span>
                </span>
                <Button
                  title={blocked ? 'Let back in' : 'Block'}
                  variant="ghost"
                  loading={busy === u.userId}
                  accessibilityLabel={blocked ? `Let ${who(u)} back in` : `Block ${who(u)}`}
                  onPress={() => (blocked ? void act(u, () => setAppUserRole(appId, u.userId, 'user')) : ask(u, 'block'))}
                />
                <Button
                  title="Remove"
                  variant="dangerSoft"
                  disabled={busy === u.userId}
                  accessibilityLabel={`Remove ${who(u)} and their data`}
                  onPress={() => ask(u, 'remove')}
                />
              </li>
            );
          })}
        </ul>
      )}

      <dialog
        ref={dialog}
        aria-label={pending ? `${pending.act === 'block' ? 'Block' : 'Remove'} ${who(pending.person)}?` : 'Confirm'}
        className="confirm-dialog"
      >
        {pending && (
          <>
            <h2 className="type-title">{`${pending.act === 'block' ? 'Block' : 'Remove'} ${who(pending.person)}?`}</h2>
            <p className="type-body">
              {pending.act === 'block'
                ? BLOCK_EXPLAINER
                : 'Everything your site holds about them goes too. To stop them without deleting anything, block them instead.'}
            </p>
          </>
        )}
        <div className="build-actions">
          <Button title={pending?.act === 'block' ? 'Block' : 'Remove'} variant="dangerSoft" onPress={confirm} />
          <Button title="Cancel" variant="ghost" onPress={() => { dialog.current?.close(); setPending(null); }} />
        </div>
      </dialog>
    </Screen>
  );
}
